import { useCallback, useEffect, useRef } from 'react'

// Per-key debounce for save callbacks. `schedule(key, run)` coalesces rapid calls for
// the same key into a single `run` after `delayMs`; the latest scheduled run wins.
// `flush()` fires any pending runs immediately — call it before submit — and it also
// runs automatically on unmount so nothing in flight is dropped.
//
// Only the SAVE callback is debounced; the form's own state/render stays synchronous,
// so typing remains responsive while the server is hit at most once per pause.
type SaveFn = () => void

interface DebouncedSaves {
  schedule: (key: number, run: SaveFn) => void
  flush: () => void
}

export const useDebouncedSaves = (delayMs: number): DebouncedSaves => {
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())
  const pending = useRef<Map<number, SaveFn>>(new Map())

  const runNow = useCallback((key: number) => {
    const run = pending.current.get(key)
    pending.current.delete(key)
    const timer = timers.current.get(key)
    if (timer != null) clearTimeout(timer)
    timers.current.delete(key)
    run?.()
  }, [])

  const schedule = useCallback((key: number, run: () => void) => {
    pending.current.set(key, run)
    const existing = timers.current.get(key)
    if (existing != null) clearTimeout(existing)
    timers.current.set(key, setTimeout(() => runNow(key), delayMs))
  }, [delayMs, runNow])

  const flush = useCallback(() => {
    Array.from(timers.current.keys()).forEach(runNow)
  }, [runNow])

  // Flush anything still pending if the form unmounts mid-debounce.
  useEffect(() => flush, [flush])

  return { schedule, flush }
}
